import { inject, Injectable } from "@angular/core";
import { map, Observable, tap } from "rxjs";
import { ComparatorOperators, GetIcsAttestationAnswerGQL, GetIcsAttestationAnswerQuery, IcsCampaignLegalEntityColumns, IcsCampaignLegalEntityQueryComplexInput, IcsCampaignLegalEntitySimpleQueryInput, IcsCampaignLegalEntitySortInput, OperatorInput, OrderTypeInput, PagingInput, UpdateChiefAttestationStatusGQL, UpdateChiefAttestationStatusMutation, UpdateGaltsAttestationStatusGQL, UpdateGaltsAttestationStatusMutation, UpdateIcsAttestationAnswer, UpdateIcsAttestationGQL, UpdateIcsAttestationMutation } from "../../../graphql/generated";
import { Apollo } from "apollo-angular";
import { DOWNLOAD_ICS_ATTESTATION } from "../../../graphql/gqls/ics-downloadGQL";

@Injectable({
    providedIn: 'root'
})
export class IcsAttestationService {
    private readonly GetIcsAttestationAnswerGQL: GetIcsAttestationAnswerGQL = inject(GetIcsAttestationAnswerGQL);
    private readonly UpdateIcsAttestationGQL: UpdateIcsAttestationGQL = inject(UpdateIcsAttestationGQL);
    private readonly UpdateGaltsAttestationStatusGQL: UpdateGaltsAttestationStatusGQL = inject(UpdateGaltsAttestationStatusGQL);
    private readonly UpdateChiefAttestationStatusGQL: UpdateChiefAttestationStatusGQL = inject(UpdateChiefAttestationStatusGQL);
    private readonly apollo: Apollo = inject(Apollo)

    getIcsAttestation(icsCampaignId: string, parameters: any): Observable<GetIcsAttestationAnswerQuery['getIcsAttestationAnswers']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetIcsAttestationAnswerGQL.fetch({ icsCampaignId, page: param.page, query: param.query, sort: param.sort }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getIcsAttestationAnswers))
    }

    updateIcsAttestation(updateAnswers: UpdateIcsAttestationAnswer[]): Observable<UpdateIcsAttestationMutation['updateIcsAttestationAnswer']> {
        return this.UpdateIcsAttestationGQL.mutate({ updateAnswers }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateIcsAttestationAnswer))
    }

    updateChiefAttestationStatus(attestationAnswerId: string, answerId: string): Observable<UpdateChiefAttestationStatusMutation['updateChiefAttestationStatus']> {
        return this.UpdateChiefAttestationStatusGQL.mutate({ answerId, attestationAnswerId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateChiefAttestationStatus))
    }

    updateGaltsAttestationStatus(attestationAnswerId: string, answerId: string): Observable<UpdateGaltsAttestationStatusMutation['updateGaltsAttestationStatus']> {
        return this.UpdateGaltsAttestationStatusGQL.mutate({ answerId, attestationAnswerId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateGaltsAttestationStatus))
    }

    downloadIcsAttestation(query: any, icsCampaignId?: string, timeZone?: string): Observable<any> {
        return this.apollo.mutate({
            mutation: DOWNLOAD_ICS_ATTESTATION,
            variables: {
                query: query,
                icsCampaignId: icsCampaignId,
                timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        }).pipe(
            map((res: any) => res.data?.downloadIcsAttestation),
            tap((response: any) => this.handleDownload(response))
        );
    }

    private handleDownload(response: any) {
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
            const file = response.data[0];
            if (file.filename && file.content) {
                const blob = this.base64ToBlob(file.content, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        }
    }

    private base64ToBlob(base64: string, mimeType: string): Blob {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: IcsCampaignLegalEntityQueryComplexInput, sort: IcsCampaignLegalEntitySortInput, page: PagingInput } {
    const query: IcsCampaignLegalEntitySimpleQueryInput[] = [];
    const queryIcs: IcsCampaignLegalEntitySimpleQueryInput[] = [];
    const legalEntitiesFields = ['legalEntity', 'businessUnit', 'legalEntityCode', 'auditControlApproach', 'finalCategory', 'status', 'questionnaireStatus', 'legalEntityName', 'businessUnit', 'organizationalUnit', 'legalEntitySize', 'businessType', 'valueChain', 'category', 'auditControlApproach', 'icsBusinessUnit', 'icsOrganizationalUnit', 'icsEntitySize', 'icsTypeOfBusiness', 'icsValueChain']
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                if (values === null || !Array.isArray(values) || values.length === 0) return;
                const isDynamicQuestionColumn = !isNaN(Number(field));
                const mapped = ENUM_ICS[field as keyof typeof ENUM_ICS];
                if (!mapped && !isDynamicQuestionColumn) {
                    console.warn(`[ics-attestation] filtro su colonna "${field}" ignorato: nessun mapping in ENUM_ICS`);
                    return;
                }
                (mapped || ENUM_ICS.icsAttestationQuestion).forEach(key => {
                    if (key === 'ICS_ATTESTATION_QUESTION') return;
                    const filteredValue: any[] = legalEntitiesFields.includes(field)
                        ? values
                        : values.length > 1 ? [] : values[0] === 'Active' ? ['true'] : ['false'];
                    query.push({
                        field: key as IcsCampaignLegalEntityColumns,
                        filters: {
                            operator: ComparatorOperators.Match,
                            value: key !== IcsCampaignLegalEntityColumns.LegalEntityCode ? filteredValue?.map(v => String(v)?.toUpperCase() === 'EMPTY' ? null : v) : filteredValue.map(v => v?.split(/\s-\s(.+)/)[0]?.trim())
                        }
                    });
                });
            });

            Object.entries(parameters.filters).forEach(([field, values]) => {
                if (values === null || !Array.isArray(values) || values.length === 0) return;
                const isDynamicQuestionColumn = !isNaN(Number(field));
                const mapped = ENUM_ICS[field as keyof typeof ENUM_ICS];
                if (!mapped && !isDynamicQuestionColumn) {
                    console.warn(`[ics-attestation] filtro su colonna "${field}" ignorato: nessun mapping in ENUM_ICS`);
                    return;
                }
                (mapped || ENUM_ICS.icsAttestationQuestion).forEach(key => {
                    if (key !== 'ICS_ATTESTATION_QUESTION') return;
                    const filteredValue = [field, ...values];
                    queryIcs.push({
                        field: key as IcsCampaignLegalEntityColumns,
                        filters: {
                            operator: key === ENUM_ICS.finalIcsEvaluationQuantitative[0] ? ComparatorOperators.Eq : ComparatorOperators.Match,
                            value: filteredValue?.map(v => String(v)?.toUpperCase() === 'EMPTY' ? null : v)
                        }
                    });
                });
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                },
                {
                    query: queryIcs,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_ICS?.[parameters?.order?.sortField as keyof typeof ENUM_ICS]?.[0] as IcsCampaignLegalEntityColumns || IcsCampaignLegalEntityColumns.LegalEntityName,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}


export const ENUM_ICS = {
    legalEntity: [IcsCampaignLegalEntityColumns.LegalEntityCode],
    businessUnit: [IcsCampaignLegalEntityColumns.BusinessUnit],
    auditControlApproach: [IcsCampaignLegalEntityColumns.AuditControlApproach],
    organizationalUnit: [IcsCampaignLegalEntityColumns.OrganizationalUnit],
    legalEntitySize: [IcsCampaignLegalEntityColumns.LegalEntitySize],
    businessType: [IcsCampaignLegalEntityColumns.BusinessType],
    valueChain: [IcsCampaignLegalEntityColumns.ValueChain],
    category: [IcsCampaignLegalEntityColumns.Category],
    legalEntityCode: [IcsCampaignLegalEntityColumns.LegalEntityCode],
    icsTopDownEvaluationStatus: [IcsCampaignLegalEntityColumns.IcsEvaluationStatus],
    icsTopDownEvaluationLocalChiefAuditorConfirmation: [IcsCampaignLegalEntityColumns.IcsEvaluationLocalChiefConfirmation],
    icsTopDownEvaluationGaltConfirmation: [IcsCampaignLegalEntityColumns.IcsEvaluationGaltConfirmation],
    icsFormalAttestationStatus: [IcsCampaignLegalEntityColumns.IcsAttestationStatus],
    icsTopDownEvaluationEvaluation: [IcsCampaignLegalEntityColumns.IcsEvaluationEvaluation],
    icsFormalAttestationLocalChiefAuditorConfirmation: [IcsCampaignLegalEntityColumns.IcsAttestationLocalChiefConfirmation],
    icsFormalAttestationGaltConfirmation: [IcsCampaignLegalEntityColumns.IcsAttestationGaltConfirmation],
    icsFormalAttestationEvaluation: [IcsCampaignLegalEntityColumns.IcsAttestationEvaluation],
    icsFormalAttestationApprovalStatus: [IcsCampaignLegalEntityColumns.IcsAttestationApprovalStatus],
    icsTopDownEvaluationApprovalStatus: [IcsCampaignLegalEntityColumns.IcsEvaluationApprovalStatus],
    startDate: [IcsCampaignLegalEntityColumns.StartDate],
    icsAuditControlApproach: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityAuditControlApproach],
    icsCategory: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityCategory],
    finalCategory: [IcsCampaignLegalEntityColumns.Category],
    // attestationScaleExplanation: [ IcsCampaignLegalEntityColumns.IcsEvaluationIcsAttestationScaleExplanation], TODO
    finalAuditControlApproach: [IcsCampaignLegalEntityColumns.AuditControlApproach],
    status: [IcsCampaignLegalEntityColumns.IcsEvaluationStatus],
    riskAssessmentEvaluations: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluation],
    auditExecutionDistributionOfEngagementGradient: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistribution],
    issueFollowUpStrikeRate: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRate],
    feedbackInspections: [IcsCampaignLegalEntityColumns.IcsEvaluationInspections],
    feedbackSanctions: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctions],
    feedbackComplaints: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaints],
    // Consolidamenti: model score (senza override), score con override e relativi quantitativi
    total: [IcsCampaignLegalEntityColumns.IcsEvaluationTotal],
    totalQuantitative: [IcsCampaignLegalEntityColumns.IcsEvaluationTotalQuantitative],
    totalModel: [IcsCampaignLegalEntityColumns.IcsEvaluationTotalModel],
    totalModelQuantitative: [IcsCampaignLegalEntityColumns.IcsEvaluationTotalModelQuantitative],
    // Il rename lato BE ha riguardato i campi dati (finalIcsEvaluation*), non le colonne di
    // filtro/sort: l'enum espone ancora i vecchi nomi CENTRAL_OVERRIDE.
    finalIcsEvaluation: [IcsCampaignLegalEntityColumns.IcsEvaluationCentralOverride],
    finalIcsEvaluationQuantitative: [IcsCampaignLegalEntityColumns.IcsEvaluationCentralOverrideValue],
    rationale: [IcsCampaignLegalEntityColumns.IcsEvaluationRationale],
    localAttestationScaleExplanation: [IcsCampaignLegalEntityColumns.IcsEvaluationLocalIcsAttestationScaleExplanation],
    localChiefConfirmationStatus: [IcsCampaignLegalEntityColumns.IcsAttestationLocalChiefConfirmation],
    galtConfirmationStatus: [IcsCampaignLegalEntityColumns.IcsAttestationGaltConfirmation],
    //ICS ATTESTATION
    questionnaireStatus: [IcsCampaignLegalEntityColumns.IcsAttestationStatus],
    legalEntityName: [IcsCampaignLegalEntityColumns.LegalEntityCode],
    icsAttestationQuestion: ['ICS_ATTESTATION_QUESTION'],

    //ICS EVALUATION QUESTIONNAIRE
    icsEvaluationQuestion: ['ICS_EVALUATION_QUESTION'],

    icsBusinessUnit: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityBusinessUnit],
    icsOrganizationalUnit: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityOrganizationalUnit],
    icsEntitySize: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityEntitySize],
    icsTypeOfBusiness: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityTypeOfBusiness],
    icsValueChain: [IcsCampaignLegalEntityColumns.IcsCampaignLegalEntityValueChain],

    // --- Colonne di dettaglio evaluation (ics-evaluation table) ---
    attestationScaleExplanation: [IcsCampaignLegalEntityColumns.IcsEvaluationIcsAttestationScaleExplanation],

    auditExecutionDistributionOfEngagementGradientOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistributionOverride],
    auditExecutionDistributionOfEngagementGradientOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistributionOverrideRationale],
    auditExecutionDistributionOfEngagementGradientQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistributionQuantitativeScore],
    auditExecutionDistributionOfEngagementGradientQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistributionQuantitativeScoreOverride],
    auditExecutionDistributionOfEngagementGradientRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationAuditExecutionDistributionRoundedScore],

    complaintsEvaluation: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaints],
    complaintsEvaluationOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsOverride],
    complaintsEvaluationQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsQuantitativeScore],
    complaintsEvaluationRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsRoundedScore],
    complaintsEvaluationOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsOverrideRationale],
    complaintsEvaluationQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsQuantitativeScoreOverride],
    complaintsPolicies: [IcsCampaignLegalEntityColumns.IcsEvaluationComplaintsPolicies],

    externalAuditorDp: [IcsCampaignLegalEntityColumns.IcsEvaluationExternalAuditorDp],

    feedbackInspectionsOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationInspectionsOverride],
    feedbackInspectionsOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationInspectionsOverrideRationale],
    feedbackInspectionsQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationInspectionsQuantitativeScore],
    feedbackInspectionsQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationInspectionsQuantitativeScoreOverride],
    feedbackInspectionsRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationInspectionsRoundedScore],

    feedbackSanctionsOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctionsOverride],
    feedbackSanctionsOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctionsOverrideRationale],
    feedbackSanctionsQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctionsQuantitativeScore],
    feedbackSanctionsQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctionsQuantitativeScoreOverride],
    feedbackSanctionsRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationSanctionsRoundedScore],

    firstMainAction: [IcsCampaignLegalEntityColumns.IcsFirstMainAction],
    firstMainActionDate: [IcsCampaignLegalEntityColumns.IcsFirstMainActionDate],
    secondMainAction: [IcsCampaignLegalEntityColumns.IcsSecondMainAction],
    secondMainActionDate: [IcsCampaignLegalEntityColumns.IcsSecondMainActionDate],
    thirdMainAction: [IcsCampaignLegalEntityColumns.IcsThirdMainAction],
    thirdMainActionDate: [IcsCampaignLegalEntityColumns.IcsThirdMainActionDate],

    fitProperties: [IcsCampaignLegalEntityColumns.IcsEvaluationFitProperties],

    girs: [IcsCampaignLegalEntityColumns.IcsEvaluationGirs],
    girsOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationGirsOverride],
    girsOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationGirsOverrideRationale],
    girsQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationGirsQuantitativeScore],
    girsQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationGirsQuantitativeScoreOverride],
    girsRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationGirsRoundedScore],

    issueFollowUpStrikeRateOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRateOverride],
    issueFollowUpStrikeRateOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRateOverrideRationale],
    issueFollowUpStrikeRateQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRateQuantitativeScore],
    issueFollowUpStrikeRateQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRateQuantitativeScoreOverride],
    issueFollowUpStrikeRateRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationIssueStrikeRateRoundedScore],

    otherKeysFunctions: [IcsCampaignLegalEntityColumns.IcsEvaluationOtherKeysFunctions],

    outlook: [IcsCampaignLegalEntityColumns.IcsOutlook],
    outlookComment: [IcsCampaignLegalEntityColumns.IcsOutlookComment],

    riskAssessmentEvaluationsOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluationOverride],
    riskAssessmentEvaluationsOverrideRationale: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluationOverrideRationale],
    riskAssessmentEvaluationsQuantitativeScore: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluationQuantitativeScore],
    riskAssessmentEvaluationsQuantitativeScoreOverride: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluationQuantitativeScoreOverride],
    riskAssessmentEvaluationsRoundedScore: [IcsCampaignLegalEntityColumns.IcsEvaluationRiskAssessmentEvaluationRoundedScore],

    toneAtTheTop: [IcsCampaignLegalEntityColumns.IcsEvaluationToneAtTheTop]
}